import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, UserCheck, Calendar, Wallet, Settings, LogOut, Menu,
  X, ChevronDown, ChevronLeft, ChevronRight, ClipboardList, FileText,
  DollarSign, Award, Bell, BarChart3, Building, FolderOpen, Briefcase,
  Clock, CalendarDays, BookOpen, MessageSquare, Star, Book,
  FileSpreadsheet, ArrowLeftCircle, CalendarCheck, Timer, Activity,
  UsersRound,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import { NotificationBell } from "./NotificationBell";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, minRole: 0 },
  { path: "/team", label: "Team Dashboard", icon: UsersRound, minRole: 3 },
  { path: "/attendance", label: "Attendance", icon: CalendarCheck, minRole: 0 },
  { path: "/tasks", label: "Tasks", icon: ClipboardList, minRole: 0 },
  { path: "/daily-reports", label: "Daily Reports", icon: FileText, minRole: 0 },
  { path: "/employees", label: "Employees", icon: Users, minRole: 3 },
  { path: "/directory", label: "Directory", icon: BookOpen, minRole: 0 },
  { path: "/approvals", label: "Approvals", icon: UserCheck, minRole: 4, badge: true },
  { path: "/jobs", label: "Jobs & Hiring", icon: Briefcase, minRole: 4 },
  { path: "/documents", label: "Documents", icon: FolderOpen, minRole: 0 },
  { path: "/leaves", label: "Leaves", icon: Calendar, minRole: 0 },
  { path: "/holidays", label: "Holidays", icon: CalendarDays, minRole: 0 },
  { path: "/shifts", label: "Shifts", icon: Timer, minRole: 3 },
  { path: "/wallet", label: "Wallet", icon: Wallet, minRole: 0 },
  { path: "/payroll", label: "Payroll", icon: DollarSign, minRole: 0 },
  { path: "/performance", label: "Performance", icon: Award, minRole: 0 },
  { path: "/recognition", label: "Recognition", icon: Star, minRole: 0 },
  { path: "/surveys", label: "Surveys", icon: MessageSquare, minRole: 0 },
  { path: "/policies", label: "Policies", icon: Book, minRole: 0 },
  { path: "/reports", label: "Reports", icon: FileSpreadsheet, minRole: 3 },
  { path: "/activity-logs", label: "Activity Logs", icon: Activity, minRole: 4 },
  { path: "/analytics", label: "Analytics", icon: BarChart3, minRole: 3 },
  { path: "/ventures", label: "Ventures", icon: Building, minRole: 5 },
  { path: "/notifications/send", label: "Communications", icon: Bell, minRole: 4 },
  { path: "/settings", label: "Settings", icon: Settings, minRole: 0 },
];

const getRoleName = (level) => {
  const roles = { 0: "Applicant", 1: "Onboarding", 2: "Employee", 3: "Manager", 4: "HRBP", 5: "Founder" };
  return roles[level] || "Employee";
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdminEmail, setOriginalAdminEmail] = useState("");
  const heartbeatRef = useRef(null);

  // Heartbeat system — sends pulse every 60 seconds
  const sendHeartbeat = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      await axios.post(`${API}/activity/heartbeat`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { /* heartbeat failure is non-critical */ }
  }, []);

  useEffect(() => {
    sendHeartbeat();
    heartbeatRef.current = setInterval(sendHeartbeat, 60000);
    const closeSession = () => {
      const token = localStorage.getItem("token");
      if (token) {
        navigator.sendBeacon?.(`${API}/activity/close-session`);
      }
    };
    window.addEventListener("beforeunload", closeSession);
    return () => {
      clearInterval(heartbeatRef.current);
      window.removeEventListener("beforeunload", closeSession);
    };
  }, [sendHeartbeat]);

  useEffect(() => {
    const impersonationMode = localStorage.getItem("impersonation_mode");
    const adminEmail = localStorage.getItem("original_admin_email");
    if (impersonationMode === "true") {
      setIsImpersonating(true);
      setOriginalAdminEmail(adminEmail || "Admin");
    }
  }, []);

  const handleReturnToAdmin = () => {
    const originalToken = localStorage.getItem("original_admin_token");
    if (originalToken) {
      localStorage.setItem("token", originalToken);
      localStorage.removeItem("original_admin_token");
      localStorage.removeItem("original_admin_email");
      localStorage.removeItem("impersonation_mode");
      toast.success("Switched back to admin account");
      window.location.href = "/employees";
    }
  };

  const userRole = user?.role_level || 0;
  const filteredNav = navItems.filter((item) => userRole >= item.minRole);
  const getInitials = () => {
    if (!user) return "U";
    return `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();
  };
  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#f5f0eb] flex">
      {/* Sidebar - Orange Theme */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 ${sidebarCollapsed ? 'w-20' : 'w-64'} bg-gradient-to-b from-[#E8913A] via-[#E07C2A] to-[#D97706] transform transition-all duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:rounded-r-3xl shadow-2xl shadow-orange-900/20`}
      >
        <div className="flex flex-col h-full relative z-10">
          {/* Logo */}
          <div className={`${sidebarCollapsed ? 'p-4' : 'p-6'} border-b border-white/20`}>
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5" />
                    <path d="M2 12l10 5 10-5" />
                  </svg>
                </div>
              </div>
              {!sidebarCollapsed && (
                <div>
                  <h1 className="text-white font-bold text-lg tracking-tight">Zulton</h1>
                  <p className="text-xs font-semibold text-white/70 tracking-widest">UNIVERSE</p>
                </div>
              )}
            </div>
          </div>

          {/* Collapse Toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-white rounded-full items-center justify-center text-orange-600 shadow-lg hover:bg-orange-50 transition-all z-20"
            data-testid="sidebar-collapse-btn"
          >
            {sidebarCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
          </button>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
            {filteredNav.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  title={sidebarCollapsed ? item.label : undefined}
                  className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} ${sidebarCollapsed ? 'px-2' : 'px-4'} py-2.5 rounded-xl transition-all duration-200 group ${
                    isActive
                      ? "bg-white text-orange-700 shadow-md shadow-orange-900/10 font-semibold"
                      : "text-white/80 hover:bg-white/15 hover:text-white"
                  }`}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-orange-600" : "text-white/70 group-hover:text-white"}`} />
                  {!sidebarCollapsed && (
                    <>
                      <span className="text-sm">{item.label}</span>
                      {item.badge && userRole >= 4 && (
                        <Badge className="ml-auto text-xs px-2 py-0.5 bg-red-500 text-white border-0">3</Badge>
                      )}
                    </>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Profile */}
          <div className={`${sidebarCollapsed ? 'p-2' : 'p-4'} border-t border-white/20`}>
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center p-2' : 'gap-3 p-3'} rounded-xl bg-white/10`}>
              <Avatar className={`${sidebarCollapsed ? 'h-8 w-8' : 'h-10 w-10'} border-2 border-white/30 flex-shrink-0`}>
                <AvatarImage src={user?.profile_image} alt={user?.first_name} />
                <AvatarFallback className="bg-white/20 text-white text-sm font-semibold">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm truncate">{user?.first_name} {user?.last_name}</p>
                  <p className="text-white/60 text-xs">{getRoleName(userRole)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 flex items-center justify-between">
            <span className="text-sm font-medium">
              Viewing as &quot;{user?.first_name} {user?.last_name}&quot; (Admin: {originalAdminEmail})
            </span>
            <Button size="sm" variant="secondary" onClick={handleReturnToAdmin}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30" data-testid="return-to-admin-btn">
              <ArrowLeftCircle className="w-4 h-4 mr-1" /> Switch back to Admin
            </Button>
          </div>
        )}

        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-orange-100/50">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
              <div>
                <h2 className="font-semibold text-slate-800 text-lg">{getPageTitle(location.pathname)}</h2>
                <p className="text-slate-500 text-sm hidden sm:block">{getGreeting()}, {user?.first_name}!</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.profile_image} alt={user?.first_name} />
                      <AvatarFallback className="bg-orange-100 text-orange-700 text-xs font-semibold">{getInitials()}</AvatarFallback>
                    </Avatar>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium text-sm">{user?.first_name} {user?.last_name}</p>
                    <p className="text-slate-500 text-xs">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`/employees/${user?.id}`)} data-testid="my-profile-menu-item">
                    <Users className="mr-2 h-4 w-4" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                    <Settings className="mr-2 h-4 w-4" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                    <LogOut className="mr-2 h-4 w-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

const getPageTitle = (pathname) => {
  const titles = {
    "/": "Dashboard", "/dashboard": "Dashboard", "/team": "Team Dashboard", "/tasks": "Tasks", "/daily-reports": "Daily Reports",
    "/employees": "Employees", "/directory": "Directory", "/approvals": "Pending Approvals",
    "/jobs": "Jobs & Hiring", "/documents": "Documents", "/leaves": "Leave Management",
    "/holidays": "Holidays", "/shifts": "Shifts", "/wallet": "My Wallet", "/payroll": "Payroll",
    "/performance": "Performance", "/recognition": "Recognition", "/surveys": "Surveys",
    "/policies": "Policies", "/reports": "Reports", "/analytics": "Analytics",
    "/ventures": "Ventures", "/notifications/send": "Communications", "/settings": "Settings",
    "/attendance": "Attendance", "/activity-logs": "Activity Logs",
  };
  return titles[pathname] || "Dashboard";
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};
